import React, { useState, useRef, useEffect } from 'react';
import StepProgressBar from '../common/stepBar';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ADDTRAILASSESMENTDATA, CALCBMI, TARGETRESULT, TRAILASSESMENT } from '../helpers/Constant';
import { getUserID, getUserTrailData } from '../utils/common';
import { postDataWithToken } from '../api/api';
import { Oval } from 'react-loader-spinner';
import DotsLoading from '../common/DotsLoading';
import { useTranslation } from 'react-i18next';

function BmiCalculator() {
    const { t } = useTranslation();
    const navigate = useNavigate(); 
    const [activeStep, setActiveStep] = useState(1);
    const [pendingStep, setPendingStep] = useState(2);

    const [weight, setWeight] = useState(0);
    const [height, setHeight] = useState(0);
    const [age, setAge] = useState(1);

    const [islbs, setIslbs] = useState(false);
    const [iscm, setIsCm] = useState(false);
    const [bmiCategory, setBmiCategory] = useState(null);
    const [isCalculating, setIsCalculating] = useState(true);
    const [bmiRes, setBmiRes] = useState(false);
    const [userData, setUserData] = useState(null);
    const [weightAPI, setWeightAPI] = useState();
    const [heightAPI, setHeightAPI] = useState();
    const [loading, setLoading] = useState(false);
    const containerRef = useRef(null);
    const containerHeightRef = useRef();

    const setBmiInfoApi = async () => {
        setLoading(true);
        setIsCalculating(false);
        const userID = getUserID();
        let userBmiInfo = {
            "user": userID,
            "weight": JSON.stringify(weightAPI),
            "height": JSON.stringify(heightAPI),
            "age": JSON.stringify(age)
        };
        try {
            const response = await postDataWithToken(CALCBMI, userBmiInfo);
            if (response.status === 200) {
                const timer = setTimeout(() => {
                    setUserData(response.data);
                    setBmiRes(true);
                    clearTimeout(timer);
                    setActiveStep(2);
                    setPendingStep(0);
                }, 2000);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }

    const ages = Array.from({ length: 120 }, (_, index) => index + 1);

    const splideRef = useRef(null);

    const getAge = () => {
        if (splideRef.current) {
            const age = splideRef.current.splide.index + 1;
            setAge(age);
        }
    };

    useEffect(() => {
        if (splideRef.current) {
            splideRef.current.splide.on('move', () => {
                getAge();
            });
        }
    }, []);

    const calculateBMI = () => {

        setIsCalculating(true);
        setBmiRes(false);
        setBmiCategory(null);
        let weightInKg = islbs ? weight * 0.453592 : weight;
        let heightInCm = iscm ? height : height * 30.48;
        setWeightAPI(parseInt(weightInKg));
        setHeightAPI(parseInt(heightInCm));
    };
    
    useEffect(() => {
        if (userData !== null) {
            if (userData.bmi < 18.5) {
                setBmiCategory(t('You are Underweight'));
            } else if (userData.bmi >= 18.5 && userData.bmi < 24.9) {
                setBmiCategory('good');
            } else if (userData.bmi >= 25 && userData.bmi < 29.9) {
                setBmiCategory(t('You are Overweight'));
            } else {
                setBmiCategory(t('You are Obese'));
            }
        }
    }, [userData, t]);


    useEffect(() => {
        if (height > 0 && weight > 0) {
            calculateBMI();
        }
    }, [height, weight, islbs, iscm]);

    useEffect(() => {
        setActiveStep(1);
        setPendingStep(2);
    }, [isCalculating]);

    const handleHeightToggle = () => {
        setIsCm(!iscm);
    
        let newHeight;
        if (iscm) {
            newHeight = (height / 30.48).toFixed(2);
            setHeight(newHeight);
        } else {
            newHeight = (height * 30.48).toFixed(2);
            setHeight(newHeight);
        }
    
        setTimeout(() => {
            let scrollPosition;

            if (iscm) {
                scrollPosition = newHeight * 8 * 10;
            } else {
                scrollPosition = newHeight * 8;
            }  
            containerHeightRef.current.scrollTop = scrollPosition;
        }, 0);
    
        calculateBMI();
    };
    
    const handleToggle = () => {
        setIslbs(!islbs);

        let newWeight;

        if (islbs) {
            newWeight =  (weight / 2.20462).toFixed(2);
            setWeight(newWeight);
        } else {
            newWeight =  (weight * 2.20462).toFixed(2);
            setWeight(newWeight);
        }

        setTimeout(() => {
            let scrollPosition;
            scrollPosition = newWeight * 8;
            containerRef.current.scrollLeft = scrollPosition;
        }, 0);

        calculateBMI();
    };

    const handleScroll = () => {
        const container = containerRef.current;
        if (container) {
            let scrollLeft = container.scrollLeft;
    
            const center = scrollLeft + 6 / 2;
            const newValue = Math.floor(center / 8);
            setWeight(newValue);
        }
    };
    
    const handleHeightScroll = () => {
        const containerDiv = containerHeightRef.current;
        const containerHeight = containerDiv.clientHeight;
        const scrollTop = containerDiv.scrollTop;
        const centerPosition = scrollTop + containerHeight / 2;

        const elements = containerDiv.querySelectorAll('.scale-number');
        let closestIndex = 0;
        let closestDistance = Math.abs(centerPosition - elements[0].offsetTop);

        elements.forEach((element, index) => {
            const distance = Math.abs(centerPosition - element.offsetTop);
            if (distance < closestDistance) {
                closestIndex = index;
                closestDistance = distance;
            }
        });

        const selectedValue = elements[closestIndex].textContent;
        setHeight(selectedValue)
    };

    const handleNext = async () => {
        const userTrailData = getUserTrailData();
        const trialData = userTrailData ? JSON.parse(userTrailData) : null;

        if (trialData) {
            const userId = getUserID();
            let data = {
                "user": userId,
                "burnedCal": (trialData.burnedCal).toString(),
                "accuracy": (trialData.accuracy).toString(),
                "time": (trialData.time).toString(),
                "heartbeat": (trialData.heartbeat).toString(),
                "score": (trialData.score).toString(),
            }

            try {
                const response = await postDataWithToken(ADDTRAILASSESMENTDATA, data);
                if (response.status === 200) {
                    localStorage.removeItem('trialData');
                    navigate(`/${TARGETRESULT}`, { replace: true });
                }
            } catch (error) {}
        }else{
            navigate(`/${TRAILASSESMENT}`, { replace: true });
        }
    }

    return (
        <div className='wrapper bg_paper px-0 pb-0 h-100'>
            {loading ?
                <div className="loader">
                    <Oval color="#daff00" secondaryColor="#ffffff" strokeWidth={3} strokeWidthSecondary={3} height={50} width={50} />
                </div>
                : ""}
            <div className='d-flex flex-column'>
                <div className='px-3'>
                    <div className='d-flex justify-content-center'>
                        <StepProgressBar activeStep={activeStep} pendingStep={pendingStep} />
                    </div>
                    <div className='d-flex flex-column jutify-content-center'>
                        <div className='bmi_info_section'>
                            <h3 className='text-white fw-bold text-uppercase'>{t('BMI info')}</h3>
                            <p className='text-white opacity-75'>
                                Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim
                            </p>
                        </div>
                        <div className='select_weight_section rounded-corners mb-3 position-relative'>
                            <div className='d-flex justify-content-between'>
                                <h6 className='text-white fw-bold opacity-75'>{t('Weight')}</h6>
                                <h5 className='selected_result fw-bold'> {islbs ? `${weight} lbs` : `${weight} kg`}</h5>
                            </div>
                            <div className='position-absolute toggle_button_wrapper'>
                                <label className="toggle_button">
                                    <input
                                        type="checkbox"
                                        checked={!islbs}
                                        onChange={handleToggle}
                                    />
                                    <div className={`slider ${!islbs ? 'first' : 'second'}`}>
                                        <div className="label text-white">lbs</div>
                                        <div className="label text-white">kg</div>
                                    </div>
                                </label>
                            </div>

                            <div
                                className="number-container text-white"
                                ref={containerRef}
                                style={{
                                    width: '100%',
                                    overflowX: 'scroll',
                                    whiteSpace: 'nowrap',
                                    padding: '4px',
                                }}
                                onScroll={handleScroll}
                            >
                                <div
                                    className="number-list"
                                    style={{
                                        display: 'inline-block',
                                        width: '100%',
                                        paddingTop: '52px'
                                    }}
                                >
                                    {Array.from({ length: islbs ? 331 : 151 }, (_, i) => i).map((num) => (
                                        <div
                                            key={num}
                                            className='scale-number'
                                            style={{
                                                display: 'inline-block',
                                                width: '8px',
                                                textAlign: 'center',
                                                padding: '0px',
                                                paddingTop: '5px',
                                            }}
                                        >
                                        {num % 5 === 0 || num === 0 ? num : ''}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='d-flex gap-3'>
                            <div className={`select_height_section rounded-corners position-relative ${iscm ? '' : 'isFeet'}`}>
                                <h6 className='text-white fw-bold mb-1 opacity-75'>{t('Height')}</h6>
                                <h5 className='selected_result fw-bold'>{iscm ? `${height} cm` : `${height} ft`}</h5>
                                <div className='position-absolute toggle_button_wrapper'>
                                    <label className="toggle_button">
                                        <input
                                            type="checkbox"
                                            checked={!iscm}
                                            onChange={handleHeightToggle}
                                        />
                                        <div className={`slider ${!iscm ? 'first' : 'second'}`}>
                                            <div className="label text-white">cm</div>
                                            <div className="label text-white">ft</div>
                                        </div>
                                    </label>
                                </div>
                                <div
                                    className="number-container text-white position-absolute"
                                    ref={containerHeightRef}
                                    style={{
                                        width: '100%',
                                        overflowY: 'auto',
                                        whiteSpace: 'nowrap',
                                        padding: '7px 4px 0',
                                        height: '158px',
                                    }}
                                    onScroll={handleHeightScroll}
                                >
                                    <div
                                        className="number-list"
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                        }}
                                    >
                                        {Array.from({ length: iscm ? 275 : 91 }, (_, i) => {
                                            if (iscm) {
                                                return i;
                                            } else {
                                                return i / 10;
                                            }
                                        }).map((num) => (<div
                                            key={num}
                                            className='scale-number'
                                            style={{
                                                height: '8px',
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'end',
                                                gap: '4px',
                                                color: iscm ? (num % 5 === 0 || num === 275 ? 'white' : 'transparent') : (num % 0.5 === 0 || num === 1 ? 'white' : 'transparent'),
                                            }}
                                        >
                                            {num}
                                        </div>
                                        ))}
                                    </div>
                                </div>

                            </div>
                            <div className='select_age_section rounded-corners position-relative'>
                                <h6 className='text-white fw-bold position-absolute opacity-75'>{t('Age')}</h6>
                                <Splide ref={splideRef} className='p-0 age-slider' aria-label="My Favorite Images" options={{ direction: 'ttb', height: '12rem', rewind: true, pagination: false, arrows: false, type: 'loop', padding: '2rem', focus: 'center', }} >
                                    {ages.map(age => (
                                        <SplideSlide key={age} >
                                            <h1 className='m-0'>{age}</h1>
                                        </SplideSlide>
                                    ))}
                                </Splide>
                            </div>
                        </div>
                    </div>
                </div>
                {
                    height > 0 && weight > 0 ?
                        isCalculating ?
                            <div className='mt-4 text-center'>
                                <button className='next_button bg-warning fw-bold text-primary' onClick={() => setBmiInfoApi()}>{t('Calculate BMI')}</button>
                            </div>
                            :
                            <div className='bmi_result_wrapper bg-primary p-4 mt-3 flex-grow-1'>
                                <div className='bmi_result border rounded-corners p-12 text-center'>
                                    <h5 className='border-bottom text-light mb-0 pb-2'>{t('Your BMI')}</h5>
                                    <div className='d-flex align-items-center justify-content-center'>
                                        {bmiRes ?
                                            <h1 className='mb-0 text-white fw-bold'>{userData.bmi}</h1>
                                            :
                                            <span className='ring_loading'></span>
                                        }
                                    </div>
                                </div>
                                <h3 className={`mb-0 py-2 text-white ${bmiCategory !== 'good' ? 'opacity-75' : ''}`}>
                                    {bmiCategory === null ?
                                        <>{t('Calculating')}< DotsLoading width={20} color={'#c5c5c5'} /></> :
                                        bmiCategory !== 'good' ?
                                            bmiCategory :
                                            <span>{t('GOOD, Keep it up')}<img src='../images/thumbIcon.png' alt='Thumb Icon' /></span>
                                    }
                                </h3>
                                <p> {
                                    bmiRes ?
                                        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim' :
                                        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation'}</p>
                                <button className='next_button bg-info fw-bold text-white d-block ms-auto' onClick={() => handleNext()}><span className='me-3' >{t('Next')}</span><FaArrowRight className='position-absolute next_arrow text-white' size={14} /></button>
                            </div>
                        : null
                }
            </div>
        </div>

    )
}

export default BmiCalculator;